/** Sentinel value rendered as a skeleton shimmer until live on-chain data arrives. */
export const LANDING_STAT_LOADING = "Loading…"

export interface LandingCta {
  readonly label: string
  readonly href: string
  readonly tone: "primary" | "secondary"
}

export interface BrutalistHeroContent {
  /** Each string is rendered on its own line of the oversized headline. */
  readonly headlineLines: readonly string[]
  /** Supporting copy under the headline; each entry is its own line. */
  readonly subcopyLines: readonly string[]
  readonly ctaLabel: string
  readonly ctaHref: string
  readonly bustImageSrc: string
}

/**
 * Connected-wallet snapshot rendered in the hero when a user is signed in and
 * holds veSCALE. Built from real on-chain data (lock repository + lock rewards)
 * by the composite; absent for logged-out visitors.
 */
export interface LandingHeroAccount {
  readonly addressLabel: string
  readonly hasLocks: boolean
  /** Total veSCALE voting power (a number, not USD). */
  readonly veScalePowerLabel: string
  /** Number of veSCALE NFTs held. */
  readonly veNftCountLabel: string
  /** Claimable rewards in USD. */
  readonly claimableValueLabel: string
  readonly hasClaimable: boolean
  readonly dashboardHref: string
  readonly ctaLabel: string
}

/** Raw recent on-chain activity row held in the store (indexer-backed). */
export interface LandingActivityRow {
  readonly id: string
  readonly valueUsd: number
  readonly account: string
  readonly timestampSec: number
  readonly txUrl: string
}

/** Display-ready activity item passed to the ticker view. */
export interface LandingActivityItem {
  readonly id: string
  readonly kindLabel: string
  readonly valueLabel: string
  readonly accountLabel: string
  readonly txUrl: string
  readonly timestampSec: number
}

export interface LandingMetric {
  readonly label: string
  readonly value: string
  readonly detail?: string
  readonly href?: string
}

export type LandingPersonaIcon = "swap" | "liquidity" | "lock" | "explore"

export interface LandingPersonaRoute {
  readonly label: string
  readonly description: string
  readonly href: string
  readonly icon: LandingPersonaIcon
}

export interface LandingTrustSignal {
  readonly label: string
  readonly href?: string
}

export interface LandingTokenLogo {
  readonly symbol: string
  readonly imageUrl: string
  readonly chainSlug: string
}

export interface LandingOpportunity {
  readonly title: string
  readonly subtitle: string
  readonly primaryValue: string
  readonly secondaryValue: string
  readonly href: string
  readonly tokenLogos?: readonly LandingTokenLogo[]
}

export interface LandingOpportunityRail {
  readonly title: string
  readonly subtitle: string
  readonly href?: string
  readonly opportunities: readonly LandingOpportunity[]
}

export interface LandingEpochCountdown {
  readonly epochNumber: number
  readonly countdownLabel: string
  readonly weekProgressPercent: number
  readonly epochIncentivesValue: string
  readonly bribeRoiValue: string
  readonly voteHref: string
}

export type LandingCapabilityIcon = "swap" | "lock" | "chart"

export interface LandingCapability {
  readonly title: string
  readonly description: string
  readonly stat?: string
  readonly icon?: LandingCapabilityIcon
}

export interface LandingTestimonial {
  readonly quote: readonly string[]
  readonly authorName: string
  readonly role: string
  readonly initials: string
}

export interface ProfessionalLandingPageViewProps {
  readonly brutalistHero: BrutalistHeroContent
  readonly heroCtas: readonly LandingCta[]
  readonly personaRoutes: readonly LandingPersonaRoute[]
  readonly epochCountdown: LandingEpochCountdown
  readonly metrics: readonly LandingMetric[]
  readonly trustSignals: readonly LandingTrustSignal[]
  readonly opportunityRails: readonly LandingOpportunityRail[]
  readonly capabilities: readonly LandingCapability[]
  readonly testimonials: readonly LandingTestimonial[]
  /** Present only when a wallet is connected with veSCALE; personalizes the hero. */
  readonly heroAccount?: LandingHeroAccount
  /** Recent on-chain activity for the live ticker; empty hides the ticker. */
  readonly activityEvents?: readonly LandingActivityItem[]
}
